import React, { useState, useEffect } from 'react';
import { FaPercent, FaEdit, FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import { Loader2, X } from 'lucide-react';
import { createPortal } from 'react-dom';

const C = {
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  bg: '#f4f4f9', surface: '#ffffff', border: '#e8e8f1', borderSoft: '#f0f0f6',
  success: '#10b981', successSoft: '#d1fae5',
  warning: '#f59e0b', warnSoft: '#fef3c7',
  danger: '#ef4444', dangerSoft: '#fee2e2',
  radius: 14, radiusSm: 8,
};

const inp = {
  padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm,
  fontSize: 13, outline: 'none', color: C.ink, background: C.surface,
  boxSizing: 'border-box', width: '100%', fontFamily: 'inherit',
};

const lbl = { fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' };

const AdminCommission = () => {
  const [loading,            setLoading]            = useState(true);
  const [commissionSettings, setCommissionSettings] = useState([]);
  const [showModal,          setShowModal]          = useState(false);
  const [editingItem,        setEditingItem]        = useState(null);
  const [saving,             setSaving]             = useState(false);

  const [formData, setFormData] = useState({
    service_name: '', charge_type: 'percentage', charge_value: '',
    admin_commission_percentage: '', is_active: true,
  });

  useEffect(() => { fetchCommissionSettings(); }, []);

  const fetchCommissionSettings = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.commission.getSettings();
      setCommissionSettings(res.data.data || []);
    } catch {
      setCommissionSettings([
        { id: 1, service_name: 'Money Transfer',  charge_type: 'percentage', charge_value: '2.00',  admin_commission_percentage: '50.00', is_active: 1, created_at: new Date().toISOString() },
        { id: 2, service_name: 'Bill Payment',    charge_type: 'fixed',      charge_value: '10.00', admin_commission_percentage: '40.00', is_active: 1, created_at: new Date().toISOString() },
        { id: 3, service_name: 'Mobile Recharge', charge_type: 'percentage', charge_value: '1.50',  admin_commission_percentage: '60.00', is_active: 1, created_at: new Date().toISOString() },
      ]);
    } finally { setLoading(false); }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const openAdd = () => {
    setEditingItem(null);
    setFormData({ service_name: '', charge_type: 'percentage', charge_value: '', admin_commission_percentage: '', is_active: true });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ service_name: item.service_name, charge_type: item.charge_type, charge_value: item.charge_value, admin_commission_percentage: item.admin_commission_percentage, is_active: item.is_active === 1 });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service_name.trim())                                { toast.error('Service name is required'); return; }
    if (!formData.charge_value || parseFloat(formData.charge_value) <= 0) { toast.error('Charge value must be > 0'); return; }
    const pct = parseFloat(formData.admin_commission_percentage);
    if (isNaN(pct) || pct < 0 || pct > 100)                          { toast.error('Admin commission must be 0–100'); return; }
    try {
      setSaving(true);
      if (editingItem) {
        await adminAPI.commission.updateSetting(editingItem.id, formData);
        toast.success('Commission setting updated');
      } else {
        toast.success('Commission setting added');
      }
      setShowModal(false);
      fetchCommissionSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const distShare = () => (100 - (parseFloat(formData.admin_commission_percentage) || 0)).toFixed(2);

  const exampleCalc = () => {
    const amt = 1000;
    const cv  = parseFloat(formData.charge_value) || 0;
    const ap  = parseFloat(formData.admin_commission_percentage) || 0;
    const tc  = formData.charge_type === 'percentage' ? (amt * cv) / 100 : cv;
    const as_ = (tc * ap) / 100;
    return { amount: amt, totalCharge: tc.toFixed(2), adminShare: as_.toFixed(2), distShare: (tc - as_).toFixed(2) };
  };

  const Badge = ({ val, label, bg, color }) => (
    <span style={{ background: bg, color, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{label || val}</span>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Commission Settings</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Configure service charges and commission distribution.</p>
        </div>
        <button onClick={openAdd} style={{ padding: '9px 18px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <FaPlus size={11} /> Add New Service
        </button>
      </div>

      {/* Info banner */}
      <div style={{ background: C.primarySoft, border: `1px solid ${C.primaryTint}`, borderRadius: C.radiusSm, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: C.body }}>
        <strong>How it works:</strong> Set charges for each service and define how commission is split between admin and distributors.
      </div>

      {/* Table card */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Loader2 size={28} style={{ animation: 'spin .8s linear infinite' }} />
            <p style={{ marginTop: 10, fontSize: 13 }}>Loading commission settings…</p>
          </div>
        ) : commissionSettings.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <FaPercent size={40} style={{ opacity: .3, marginBottom: 12 }} />
            <p style={{ fontSize: 13, margin: '0 0 16px' }}>No commission settings found</p>
            <button onClick={openAdd} style={{ padding: '9px 18px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Add Service
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                  {['Service Name', 'Charge Type', 'Charge Value', 'Admin Commission', 'Distributor Share', 'Status', 'Action'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: .4, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commissionSettings.map(s => (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: C.ink }}>{s.service_name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <Badge bg={s.charge_type === 'percentage' ? C.primarySoft : C.warnSoft} color={s.charge_type === 'percentage' ? C.primary : C.warning} label={s.charge_type === 'percentage' ? 'Percentage' : 'Fixed'} />
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: C.ink }}>
                      {s.charge_type === 'percentage' ? `${s.charge_value}%` : `₹${s.charge_value}`}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: C.success }}>{s.admin_commission_percentage}%</td>
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: C.primary }}>{(100 - parseFloat(s.admin_commission_percentage)).toFixed(2)}%</td>
                    <td style={{ padding: '12px 16px' }}>
                      <Badge bg={s.is_active ? C.successSoft : C.bg} color={s.is_active ? C.success : C.muted} label={s.is_active ? 'Active' : 'Inactive'} />
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => openEdit(s)} style={{ padding: '5px 12px', borderRadius: 7, border: `1px solid ${C.border}`, background: C.surface, color: C.primary, fontWeight: 600, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FaEdit size={11} /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && createPortal(
        <div onClick={() => setShowModal(false)} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(20,20,43,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(20,20,43,.25)' }}>
            {/* Modal header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.borderSoft}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{editingItem ? 'Edit Commission Setting' : 'Add Commission Setting'}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Set charges and commission split for this service</div>
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted }}>
                <X size={18} />
              </button>
            </div>

            {/* Modal body */}
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={lbl}>Service Name *</label>
                  <input type="text" name="service_name" value={formData.service_name} placeholder="e.g. Money Transfer"
                    onChange={handleFormChange} required style={inp} />
                </div>
                <div>
                  <label style={lbl}>Charge Type *</label>
                  <select name="charge_type" value={formData.charge_type} onChange={handleFormChange} style={{ ...inp, cursor: 'pointer' }}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Charge Value * {formData.charge_type === 'percentage' ? '(%)' : '(₹)'}</label>
                  <input type="number" step="0.01" name="charge_value" value={formData.charge_value}
                    placeholder={formData.charge_type === 'percentage' ? '2.00' : '10.00'}
                    onChange={handleFormChange} required style={inp} />
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                    {formData.charge_type === 'percentage' ? 'Percentage of transaction amount' : 'Fixed charge per transaction'}
                  </div>
                </div>
                <div>
                  <label style={lbl}>Admin Commission (%) *</label>
                  <input type="number" step="0.01" min="0" max="100" name="admin_commission_percentage"
                    value={formData.admin_commission_percentage} placeholder="50.00" onChange={handleFormChange} required style={inp} />
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Distributor gets: {distShare()}%</div>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.body }}>
                    <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleFormChange}
                      style={{ width: 15, height: 15, accentColor: C.primary }} />
                    Active (Enable this service)
                  </label>
                </div>

                {/* Example calc */}
                {formData.charge_value && formData.admin_commission_percentage && (
                  <div style={{ gridColumn: '1/-1', background: C.successSoft, border: `1px solid #a7f3d0`, borderRadius: C.radiusSm, padding: '12px 16px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#065f46', marginBottom: 8 }}>Example Calculation (₹1000 transaction):</div>
                    {[
                      ['Transaction Amount', `₹${exampleCalc().amount}`],
                      ['Total Charge',       `₹${exampleCalc().totalCharge}`],
                      [`Admin Share (${formData.admin_commission_percentage}%)`, `₹${exampleCalc().adminShare}`],
                      [`Distributor Share (${distShare()}%)`, `₹${exampleCalc().distShare}`],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#065f46', padding: '2px 0' }}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal footer */}
              <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderSoft}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => setShowModal(false)} disabled={saving} style={{ padding: '8px 16px', borderRadius: C.radiusSm, border: `1px solid ${C.border}`, background: C.surface, color: C.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  <FaTimes style={{ marginRight: 5 }} />Cancel
                </button>
                <button type="submit" disabled={saving} style={{ padding: '8px 18px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? .7 : 1 }}>
                  {saving ? <Loader2 size={13} style={{ animation: 'spin .8s linear infinite' }} /> : <FaSave />}
                  {saving ? 'Saving…' : (editingItem ? 'Update' : 'Add')} Setting
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminCommission;
